import { useCallback, useEffect, useLayoutEffect, useRef, useState, type CSSProperties, type RefObject } from 'react';

export type MenuPlacementPreference = 'auto' | 'top' | 'bottom';

type MenuPlacement = {
  style: CSSProperties;
  menuRef: RefObject<HTMLDivElement | null>;
};

export function useMenuPortal(
  open: boolean,
  onClose: () => void,
  anchorRef: RefObject<HTMLElement | null>,
  placement: MenuPlacementPreference = 'auto',
) {
  const menuRef = useRef<HTMLDivElement | null>(null);
  const [style, setStyle] = useState<CSSProperties>({ visibility: 'hidden' });

  const updatePosition = useCallback(() => {
    const anchor = anchorRef.current;
    if (!anchor) return;
    const rect = anchor.getBoundingClientRect();
    const gap = 4;
    const maxHeight = 320;
    const spaceBelow = window.innerHeight - rect.bottom - gap;
    const spaceAbove = rect.top - gap;
    const openUp =
      placement === 'top' ||
      (placement === 'auto' && spaceBelow < 220 && spaceAbove > spaceBelow);

    setStyle({
      position: 'fixed',
      left: rect.left,
      width: Math.max(rect.width, 200),
      minWidth: rect.width,
      zIndex: 10000,
      visibility: 'visible',
      ...(openUp
        ? {
            bottom: window.innerHeight - rect.top + gap,
            maxHeight: Math.min(maxHeight, spaceAbove),
          }
        : {
            top: rect.bottom + gap,
            maxHeight: Math.min(maxHeight, spaceBelow),
          }),
    });
  }, [anchorRef, placement]);

  useLayoutEffect(() => {
    if (!open) return;
    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [open, updatePosition]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      const target = e.target as Node;
      if (anchorRef.current?.contains(target) || menuRef.current?.contains(target)) return;
      const el = target as Element;
      if (el.closest?.('.academia-select-menu')) return;
      onClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, onClose, anchorRef]);

  return { menuRef, style, updatePosition } satisfies MenuPlacement & { updatePosition: () => void };
}
